function normalizeStatusName(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function toMillis(value) {
  if (!value) return NaN;
  const raw = String(value).trim().replace(' ', 'T');
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : NaN;
}

function secondsBetween(from, to) {
  const a = toMillis(from);
  const b = toMillis(to);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.max(0, Math.round((b - a) / 1000));
}

function isResolvedLike(value) {
  return /\b(resolved|complete|completed|done|fulfilled|restored|fixed|delivered)\b/.test(normalizeStatusName(value));
}

function isTerminalLike(value) {
  return /\b(closed|cancelled|canceled|invalid|rejected|duplicate|withdrawn|archived|finished)\b/.test(normalizeStatusName(value));
}

function mappingForActivity(activity, mappingByName) {
  const normalized = normalizeStatusName(activity);
  const mapped = mappingByName.get(normalized);
  if (mapped) return mapped;
  return {
    statusName: String(activity || ''),
    normalizedStatus: normalized,
    operationalType: 'unclassified',
    timeBucket: 'unclassified',
    confidence: 0,
    matchSource: 'canonical_unmapped',
    reason: 'No lifecycle mapping supplied for canonical activity',
    flags: {},
  };
}

function bucketForInterval(current, next) {
  const bucket = current?.timeBucket || 'unclassified';
  if (bucket !== 'unclassified') return bucket;

  // Defensive parity with the mature legacy implementation: a resolved-like
  // state followed by closure represents post-resolution administrative dwell.
  if (isResolvedLike(current?.statusName) && isTerminalLike(next?.statusName)) return 'admin';
  if (isResolvedLike(current?.statusName)) return 'admin';
  return 'unclassified';
}

function emptyDurations() {
  return {
    touchSeconds: 0,
    waitSeconds: 0,
    adminSeconds: 0,
    queueSeconds: 0,
    unclassifiedSeconds: 0,
  };
}

/**
 * Promote Process Advisor lifecycle/status semantics into the canonical model.
 *
 * `statusMappings` is intentionally supplied by the host Process Advisor runtime
 * so @metricus/canonical-process remains platform/UI independent and does not
 * depend back on @process-advisor.
 *
 * Dwell time belongs to the activity that is active between the current event
 * timestamp and the next event timestamp. The final event has no observed dwell.
 */
export function enrichCanonicalLifecycleDataset(dataset, { statusMappings = [], activityContext = null } = {}) {
  if (!dataset) return dataset;

  const mappingByName = new Map(
    (statusMappings || []).map(mapping => [
      normalizeStatusName(mapping?.statusName ?? mapping?.name ?? mapping?.activity),
      mapping,
    ])
  );

  const meaningfulMappings = (statusMappings || []).filter(m => {
    const bucket = String(m?.timeBucket || 'unclassified');
    return !['unclassified', 'no_time'].includes(bucket);
  });
  const lifecycleAvailable = meaningfulMappings.length > 0;

  const caseById = new Map((dataset.cases || []).map(c => [String(c.id), c]));
  const grouped = new Map();
  for (const event of dataset.events || []) {
    const key = String(event?.caseId || '');
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(event);
  }

  const eventUpdates = new Map();
  const caseUpdates = new Map();

  for (const [caseId, rawEvents] of grouped.entries()) {
    const events = rawEvents.slice().sort((a, b) => {
      const ta = toMillis(a?.timestamp);
      const tb = toMillis(b?.timestamp);
      if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
      return String(a?.id || '').localeCompare(String(b?.id || ''));
    });

    const durations = emptyDurations();
    let firstActiveAt = null;
    let firstWaitAt = null;
    let firstResolvedAt = null;
    let firstClosedAt = null;
    let firstDoneAt = null;

    for (let i = 0; i < events.length; i += 1) {
      const event = events[i];
      const next = events[i + 1] || null;
      const mapping = mappingForActivity(event?.activity, mappingByName);
      const nextMapping = next ? mappingForActivity(next?.activity, mappingByName) : null;
      const dwellSeconds = next ? secondsBetween(event?.timestamp, next?.timestamp) : 0;
      const durationBucket = bucketForInterval(mapping, nextMapping);

      if (mapping.timeBucket === 'touch' && !firstActiveAt) firstActiveAt = event.timestamp || null;
      if (mapping.timeBucket === 'wait' && !firstWaitAt) firstWaitAt = event.timestamp || null;
      if (mapping.operationalType === 'resolved' && !firstResolvedAt) firstResolvedAt = event.timestamp || null;
      if (mapping.operationalType === 'closed' && !firstClosedAt) firstClosedAt = event.timestamp || null;
      if (['resolved', 'closed'].includes(mapping.operationalType) && !firstDoneAt) firstDoneAt = event.timestamp || null;

      if (durationBucket === 'touch') durations.touchSeconds += dwellSeconds;
      else if (durationBucket === 'wait') durations.waitSeconds += dwellSeconds;
      else if (durationBucket === 'admin') durations.adminSeconds += dwellSeconds;
      else if (durationBucket === 'queue') durations.queueSeconds += dwellSeconds;
      else if (!['terminal', 'no_time'].includes(durationBucket)) durations.unclassifiedSeconds += dwellSeconds;

      eventUpdates.set(String(event.id), {
        ...event,
        extensions: {
          ...(event.extensions || {}),
          lifecycle: {
            statusName: mapping.statusName || event.activity || '',
            normalizedStatus: mapping.normalizedStatus || normalizeStatusName(event.activity),
            operationalType: mapping.operationalType || 'unclassified',
            timeBucket: mapping.timeBucket || 'unclassified',
            confidence: Number(mapping.confidence || 0),
            mappingSource: mapping.matchSource || mapping.mappingSource || 'canonical_unmapped',
            mappingReason: mapping.reason || '',
            dwellSeconds,
            durationBucket,
            nextStatusName: nextMapping?.statusName || next?.activity || '',
            nextOperationalType: nextMapping?.operationalType || '',
            nextTimeBucket: nextMapping?.timeBucket || '',
          },
        },
      });
    }

    const c = caseById.get(caseId) || {};
    const createdAt = c?.createdAt || events?.[0]?.timestamp || null;
    const closedAt = c?.closedAt || events?.[events.length - 1]?.timestamp || null;
    const startAt = firstActiveAt || createdAt;
    const doneAt = firstDoneAt || firstClosedAt || closedAt;
    const resolutionAt = firstResolvedAt || firstDoneAt || firstClosedAt || closedAt;
    const cycleStartedSeconds = secondsBetween(startAt, doneAt);
    const resolutionSeconds = secondsBetween(createdAt, resolutionAt);
    const leadSeconds = secondsBetween(createdAt, closedAt);
    const knownFlowSeconds = durations.touchSeconds + durations.waitSeconds + durations.adminSeconds + durations.queueSeconds;
    const flowEfficiency = knownFlowSeconds > 0 ? Math.min(1, durations.touchSeconds / knownFlowSeconds) : 0;

    caseUpdates.set(caseId, {
      ...c,
      extensions: {
        ...(c.extensions || {}),
        lifecycle: {
          available: lifecycleAvailable,
          firstActiveAt,
          firstWaitAt,
          firstResolvedAt,
          firstClosedAt,
          firstDoneAt,
          cycleStartedSeconds,
          resolutionSeconds,
          leadSeconds,
          ...durations,
          knownFlowSeconds,
          flowEfficiency,
          mappingCount: statusMappings?.length || 0,
          mappedActivityCount: meaningfulMappings.length,
        },
      },
    });
  }

  const cases = (dataset.cases || []).map(c => caseUpdates.get(String(c.id)) || {
    ...c,
    extensions: {
      ...(c.extensions || {}),
      lifecycle: {
        available: lifecycleAvailable,
        ...emptyDurations(),
        cycleStartedSeconds: 0,
        resolutionSeconds: 0,
        leadSeconds: 0,
        knownFlowSeconds: 0,
        flowEfficiency: 0,
        mappingCount: statusMappings?.length || 0,
        mappedActivityCount: meaningfulMappings.length,
      },
    },
  });
  const events = (dataset.events || []).map(e => eventUpdates.get(String(e.id)) || e);

  return {
    ...dataset,
    cases,
    events,
    metadata: {
      ...(dataset.metadata || {}),
      lifecycle: {
        available: lifecycleAvailable,
        mappingCount: statusMappings?.length || 0,
        mappedActivityCount: meaningfulMappings.length,
        activityContext: activityContext || null,
        derivedAt: new Date().toISOString(),
        derivation: 'canonical-event-dwell-v1',
      },
    },
  };
}

export function deriveCanonicalLifecycleSummary(dataset) {
  const cases = dataset?.cases || [];
  const lifecycles = cases.map(c => c?.extensions?.lifecycle).filter(l => l?.available);
  if (!lifecycles.length) {
    return {
      available: false,
      caseCount: cases.length,
      lifecycleCaseCount: 0,
      avgCycleStartedSeconds: null,
      avgResolutionSeconds: null,
      avgTouchSeconds: null,
      avgWaitSeconds: null,
      avgAdminSeconds: null,
      avgQueueSeconds: null,
      avgUnclassifiedSeconds: null,
      flowEfficiency: null,
    };
  }

  const avg = key => lifecycles.reduce((sum, lifecycle) => sum + Number(lifecycle?.[key] || 0), 0) / lifecycles.length;
  const totalTouch = lifecycles.reduce((sum, lifecycle) => sum + Number(lifecycle?.touchSeconds || 0), 0);
  const totalKnown = lifecycles.reduce((sum, lifecycle) => sum + Number(lifecycle?.knownFlowSeconds || 0), 0);

  return {
    available: true,
    caseCount: cases.length,
    lifecycleCaseCount: lifecycles.length,
    avgCycleStartedSeconds: avg('cycleStartedSeconds'),
    avgResolutionSeconds: avg('resolutionSeconds'),
    avgTouchSeconds: avg('touchSeconds'),
    avgWaitSeconds: avg('waitSeconds'),
    avgAdminSeconds: avg('adminSeconds'),
    avgQueueSeconds: avg('queueSeconds'),
    avgUnclassifiedSeconds: avg('unclassifiedSeconds'),
    flowEfficiency: totalKnown > 0 ? Math.min(1, totalTouch / totalKnown) : 0,
  };
}
